import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { subscriptionService } from "@/services/subscription.service";
import { moduleService } from "@/services/module.service";
import type { PlanPayload } from "@/types";
import type { SystemModule, ModuleCategory } from "@/types/modules";
import toast from "react-hot-toast";

export interface PlanFormData extends PlanPayload {
  selectedModules: string[];
}

export const PLAN_TYPES = [
  "STARTER",
  "GROWTH",
  "PROFESSIONAL",
  "ENTERPRISE",
  "TRIAL",
] as const;

const emptyPlan: PlanPayload = {
  name: "",
  type: "STARTER",
  description: "",
  priceMonthly: 0,
  priceYearly: 0,
  maxStores: 1,
  maxUsers: 1,
  storageGB: 5,
  features: [],
  trialDays: 0,
  maxOfflineDays: 7,
  isActive: true,
};

export const emptyFormData: PlanFormData = {
  ...emptyPlan,
  selectedModules: [],
};

export function usePlanManager() {
  const queryClient = useQueryClient();

  const { data: plans, isLoading } = useQuery({
    queryKey: ["admin-plans", "all"],
    queryFn: () => subscriptionService.listPlans(true),
  });

  const { data: modules } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: moduleService.getAllModules,
  });

  const [form, setForm] = useState<PlanFormData>({ ...emptyFormData });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showModuleSelector, setShowModuleSelector] = useState(false);
  const [moduleSearchTerm, setModuleSearchTerm] = useState("");

  const { mutateAsync: createPlan, isPending: isCreating } = useMutation({
    mutationFn: async (data: PlanFormData) => {
      const plan = await subscriptionService.createPlan({
        name: data.name,
        type: data.type,
        description: data.description,
        priceMonthly: data.priceMonthly,
        priceYearly: data.priceYearly,
        maxStores: data.maxStores,
        maxUsers: data.maxUsers,
        storageGB: data.storageGB,
        features: data.features ?? [],
        trialDays: data.trialDays,
        maxOfflineDays: data.maxOfflineDays,
        isActive: data.isActive,
      });
      if (data.selectedModules.length > 0) {
        await moduleService.updatePlanModules(plan.id, data.selectedModules);
      }
      return plan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      setForm({ ...emptyFormData });
      setShowModuleSelector(false);
      toast.success("Plan created with modules");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create plan";
      toast.error(message);
    },
  });

  const { mutateAsync: updatePlan, isPending: isUpdating } = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PlanFormData;
    }) => {
      const plan = await subscriptionService.updatePlan(id, {
        name: payload.name,
        type: payload.type,
        description: payload.description,
        priceMonthly: payload.priceMonthly,
        priceYearly: payload.priceYearly,
        maxStores: payload.maxStores,
        maxUsers: payload.maxUsers,
        storageGB: payload.storageGB,
        features: payload.features ?? [],
        trialDays: payload.trialDays,
        maxOfflineDays: payload.maxOfflineDays,
        isActive: payload.isActive,
      });
      await moduleService.updatePlanModules(id, payload.selectedModules);
      return plan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success("Plan updated");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update plan";
      toast.error(message);
    },
  });

  const handleSubmit = async () => {
    await createPlan(form);
  };

  const filteredModules = useMemo(() => {
    if (!modules) return [];
    if (!moduleSearchTerm) return modules;
    const term = moduleSearchTerm.toLowerCase();
    return modules.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.code.toLowerCase().includes(term),
    );
  }, [modules, moduleSearchTerm]);

  const groupedModules = useMemo(() => {
    return filteredModules.reduce(
      (acc, module) => {
        const category = module.category as ModuleCategory;
        if (!acc[category]) acc[category] = [];
        acc[category].push(module);
        return acc;
      },
      {} as Record<ModuleCategory, SystemModule[]>,
    );
  }, [filteredModules]);

  const toggleModule = (
    moduleCode: string,
    setter: React.Dispatch<React.SetStateAction<PlanFormData>>,
  ) => {
    setter((prev) => ({
      ...prev,
      selectedModules: prev.selectedModules.includes(moduleCode)
        ? prev.selectedModules.filter((c) => c !== moduleCode)
        : [...prev.selectedModules, moduleCode],
    }));
  };

  const planRows = useMemo(() => plans ?? [], [plans]);

  return {
    // Data
    plans,
    planRows,
    modules,
    isLoading,
    // Create form
    form,
    setForm,
    showCreateForm,
    setShowCreateForm,
    showModuleSelector,
    setShowModuleSelector,
    moduleSearchTerm,
    setModuleSearchTerm,
    // Mutations
    createPlan,
    isCreating,
    updatePlan,
    isUpdating,
    handleSubmit,
    // Module helpers
    filteredModules,
    groupedModules,
    toggleModule,
  };
}
